<!-- src/components/Inventario/Reportes/ScrapAlmacenLaser/components/SALGraficaBarras.vue -->
<template>
  <div class="sal-grafica-wrapper" :style="{ height: alturaWrapper }">
    <canvas v-show="datosA.length || datosB.length" ref="canvasRef"></canvas>
    <div v-if="!datosA.length && !datosB.length" class="sal-grafica-empty">
      Sin datos para el periodo / productos seleccionados
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue";
import { Chart, registerables } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

Chart.register(...registerables, ChartDataLabels);

const props = defineProps({
  datosA: { type: Array, default: () => [] },
  datosB: { type: Array, default: () => [] },
  titulo: { type: String, default: "Piezas de Scrap reportadas en Almacén y Láser" },
  subtitulo: { type: String, default: "" },
  filtroActivo: { type: Boolean, default: false }, // true = productos específicos seleccionados
});

const COLOR_A = "#7b2637"; // vino oscuro
const COLOR_B = "#c9748a"; // vino claro

function formatearPiezas(valor) {
  if (!valor && valor !== 0) return "";
  return new Intl.NumberFormat("es-MX", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(valor);
}

// Unificar productos de A y B, ordenados por A+B descendente.
// Sin filtro activo: top 5. Con filtro activo: todos los seleccionados.
const productosUnificados = computed(() => {
  const todos = new Set([
    ...props.datosA.map((d) => d.producto),
    ...props.datosB.map((d) => d.producto),
  ]);
  const mapaA = Object.fromEntries(
    props.datosA.map((d) => [d.producto, d.cantidad_scrap])
  );
  const mapaB = Object.fromEntries(
    props.datosB.map((d) => [d.producto, d.cantidad_scrap])
  );
  const ordenados = [...todos]
    .map((p) => ({ producto: p, a: mapaA[p] ?? 0, b: mapaB[p] ?? 0 }))
    .sort((x, y) => y.a + y.b - (x.a + x.b));

  return props.filtroActivo ? ordenados : ordenados.slice(0, 5);
});

const alturaWrapper = computed(() => {
  const n = productosUnificados.value.length || 1;
  return `${Math.max(280, n * 50 + 80)}px`;
});

const canvasRef = ref(null);
let chartInstance = null;

function buildConfig() {
  const items = productosUnificados.value;
  return {
    type: "bar",
    data: {
      labels: items.map((d) => d.producto),
      datasets: [
        {
          label: "Periodo A",
          data: items.map((d) => d.a),
          backgroundColor: COLOR_A,
          datalabels: {
            display: (ctx) => (ctx.parsed?.x ?? 0) > 0,
            color: "#fff",
            anchor: "end",
            align: "start",
            font: { weight: "bold", size: 10 },
            formatter: (v) => formatearPiezas(v),
          },
        },
        {
          label: "Periodo B",
          data: items.map((d) => d.b),
          backgroundColor: COLOR_B,
          datalabels: {
            display: (ctx) => (ctx.parsed?.x ?? 0) > 0,
            color: "#fff",
            anchor: "end",
            align: "start",
            font: { weight: "bold", size: 10 },
            formatter: (v) => formatearPiezas(v),
          },
        },
      ],
    },
    options: {
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        title: {
          display: true,
          text: props.titulo,
          font: { size: 14, weight: "bold" },
        },
        subtitle: {
          display: !!props.subtitulo,
          text: props.subtitulo,
          font: { size: 12 },
          padding: { bottom: 8 },
        },
        legend: {
          display: true,
          position: "bottom",
          labels: { usePointStyle: true },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${formatearPiezas(ctx.parsed.x)} pzas`,
          },
        },
        datalabels: { display: false }, // override por dataset arriba
      },
      scales: {
        y: { grid: { display: false } },
        x: {
          beginAtZero: true,
          title: { display: true, text: "Piezas" },
        },
      },
    },
  };
}

function destroyChart() {
  if (chartInstance) {
    chartInstance.destroy();
    chartInstance = null;
  }
}
function renderChart() {
  destroyChart();
  if (!canvasRef.value || !productosUnificados.value.length) return;
  chartInstance = new Chart(canvasRef.value, buildConfig());
}

onMounted(() => nextTick(renderChart));
onBeforeUnmount(() => destroyChart());

watch(
  () => [props.datosA, props.datosB, props.titulo, props.subtitulo],
  () => {
    destroyChart();
    nextTick(renderChart);
  },
  { deep: true }
);
</script>

<style scoped>
.sal-grafica-wrapper {
  position: relative;
  width: 100%;
  transition: height 0.15s ease;
}
.sal-grafica-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  font-size: 0.9rem;
}
</style>
